import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Bot,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Database,
  Gamepad2,
  Layers3,
  MessageCircleMore,
  Plus,
  Radio,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import { Link } from "../lib/router";
import { api } from "../lib/api";
import { Button, EmptyState, ErrorState, LoadingState, MetricCard, SectionCard, StatusBadge } from "../components/UI";

function formatStorage(bytes: number) {
  if (bytes <= 0) return "0 MB";
  const mb = bytes / 1024 / 1024;
  return mb > 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`;
}

function timeAgo(value: string) {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60_000));
  if (minutes < 1) return "Baru saja";
  if (minutes < 60) return `${minutes} menit lalu`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)} jam lalu`;
  return `${Math.floor(minutes / 1440)} hari lalu`;
}

export function Dashboard() {
  const query = useQuery({ queryKey: ["dashboard"], queryFn: api.dashboard, refetchInterval: 30_000 });

  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState message={query.error?.message} retry={() => void query.refetch()} />;
  const { totals, performance, activity, trend, activeGame } = query.data;
  const maxTrend = Math.max(1, ...trend.map((item) => item.rounds));

  return (
    <div className="dashboard-page">
      <div className="dashboard-intro">
        <p>Kendalikan konten, grup, dan ronde Telegram dari satu workspace yang aman.</p>
        <div>
          <Link to="/live"><Button variant="secondary" icon={Radio}>Pantau live</Button></Link>
          <Link to="/questions/new"><Button icon={Plus}>Buat soal</Button></Link>
        </div>
      </div>

      <div className="metric-grid">
        <MetricCard label="Total pertanyaan" value={totals.questions} hint={`${totals.published} siap dimainkan`} icon={ClipboardList} tone="gold" />
        <MetricCard label="Varian aktif" value={totals.variants} hint="Clue terkurasi" icon={Layers3} tone="violet" />
        <MetricCard label="Grup terhubung" value={totals.groups} hint="Bot aktif di Telegram" icon={MessageCircleMore} tone="blue" />
        <MetricCard label="Pemain unik" value={totals.players} hint={`${totals.roundsToday} ronde hari ini`} icon={Users} tone="green" />
      </div>

      <div className="dashboard-grid">
        <SectionCard
          title="Performa permainan"
          subtitle="Aktivitas ronde selama tujuh hari terakhir"
          action={<div className="chart-legend"><span><i className="rounds" />Ronde</span><span><i className="solved" />Terjawab</span></div>}
          className="performance-card"
        >
          <div className="performance-summary">
            <div><strong>{totals.roundsToday}</strong><span>Ronde hari ini</span></div>
            <div><strong>{performance.correctRate.toFixed(1)}%</strong><span>Success rate</span></div>
            <div><strong>{performance.timeoutRate.toFixed(1)}%</strong><span>Timeout rate</span></div>
            <div><strong>{formatStorage(performance.storageBytes)}</strong><span>Media tersimpan</span></div>
          </div>
          <div className="trend-chart" aria-label="Grafik permainan tujuh hari">
            {trend.map((item) => (
              <div className="trend-column" key={item.label}>
                <div className="trend-bars">
                  <span className="bar rounds" style={{ height: `${Math.max(5, (item.rounds / maxTrend) * 100)}%` }} />
                  <span className="bar solved" style={{ height: `${Math.max(3, (item.solved / maxTrend) * 100)}%` }} />
                </div>
                <small>{item.label}</small>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Ronde aktif" subtitle="Status server real-time" action={activeGame && <StatusBadge status="active" />} className="live-round-card">
          {activeGame ? (
            <div className="active-round">
              <div className="round-visual"><Gamepad2 size={28} /><span className="round-ring" /></div>
              <span className="round-kicker">Sedang berlangsung</span>
              <h3>{activeGame.chatTitle}</h3>
              <p>{activeGame.variant} · {activeGame.attempts} jawaban masuk</p>
              <div className="round-stats">
                <div><Clock3 size={15} /><span>Sisa waktu<strong>{Math.max(0, Math.ceil((activeGame.expiresAt - Date.now()) / 1000))} detik</strong></span></div>
                <div><Users size={15} /><span>Pemain<strong>{activeGame.players}</strong></span></div>
              </div>
              <Link to="/live"><Button variant="secondary">Buka monitor <ArrowRight size={15} /></Button></Link>
            </div>
          ) : (
            <EmptyState title="Tidak ada ronde aktif" description="Mulai permainan kapan saja dari bank soal atau halaman live game." action={<Link to="/live"><Button variant="secondary">Mulai ronde</Button></Link>} />
          )}
        </SectionCard>
      </div>

      <div className="dashboard-lower-grid">
        <SectionCard title="Aktivitas terbaru" subtitle="Jejak perubahan penting di workspace" action={<Link className="text-link" to="/audit">Lihat semua <ArrowRight size={13} /></Link>}>
          {activity.length ? (
            <div className="activity-list">
              {activity.slice(0, 6).map((item) => (
                <div className="activity-row" key={item.id}>
                  <span className={`activity-icon ${item.type.includes("game") ? "green" : item.type.includes("media") ? "violet" : "gold"}`}>
                    {item.type.includes("game") ? <Gamepad2 size={15} /> : item.type.includes("media") ? <Database size={15} /> : <CheckCircle2 size={15} />}
                  </span>
                  <span><strong>{item.title}</strong><small>{item.detail}</small></span>
                  <time>{timeAgo(item.createdAt)}</time>
                </div>
              ))}
            </div>
          ) : <EmptyState title="Belum ada aktivitas" description="Aktivitas penting akan muncul setelah Anda membuat soal, menghubungkan grup, atau memulai game." />}
        </SectionCard>

        <SectionCard title="Aksi cepat" subtitle="Jalur singkat untuk pekerjaan rutin">
          <div className="quick-actions">
            <Link to="/questions/new"><span className="quick-icon gold"><Sparkles size={18} /></span><span><strong>Siapkan soal baru</strong><small>Unggah, crop, dan publikasikan</small></span><ArrowRight size={15} /></Link>
            <Link to="/live"><span className="quick-icon green"><Target size={18} /></span><span><strong>Mulai ronde</strong><small>Pilih grup dan pertanyaan</small></span><ArrowRight size={15} /></Link>
            <Link to="/groups"><span className="quick-icon blue"><Bot size={18} /></span><span><strong>Hubungkan grup</strong><small>Buat connect code sekali pakai</small></span><ArrowRight size={15} /></Link>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
